import {CustomerContact} from "App/services/api";
import {FetchAction, FetchState, getConfigurationCookie} from "App/services";
import {ActionType, ContactType, VALUE_NORMALIZED_SUFFIX} from "App/constants";
import {asString, selectedGlobalLang} from "App/helper";
import {formatPhoneNumberIntl} from 'react-phone-number-input'
import {countryByLanguage, CountryCodeAction} from "App/country";

// Form for a contact configuration
export interface ContactData {
    contact: CustomerContact
    formReady: boolean
    formSubmitting: boolean
    formSubmitted: boolean
    submissionError: boolean
    subscribed?: boolean
}

export const initialState: ContactData = {
    contact: {},
    formReady:false,
    formSubmitting: false,
    formSubmitted: false,
    submissionError: false
};

export enum FormField {
    EMAIL = 'email',
    PHONE = 'phone',
    TITLE = 'title',
    GIVENNAME = 'givenname',
    FAMILYNAME = 'familyname',
    COMPANYNAME = 'companyname',
    ADDRESS1 = 'address1',
    ADDRESS2 = 'address2',
    ZIPCODE = 'zipcode',
    CITY = 'city',
    COUNTRY = 'country',
    COMMENTS = 'comments',
    ACCOUNTREQUEST = 'newportalaccountrequest'
}

function normalizeFieldValue(field: FormField, value: string | boolean): string | boolean{
    switch (field) {
        case FormField.PHONE:
            return formatPhoneNumberIntl(asString(value));
        case FormField.COUNTRY:
            return countryByLanguage(selectedGlobalLang(), asString(value));
        case FormField.ACCOUNTREQUEST:
            return value;
        default:
            return value?asString(value).trim():value;
    }
}

export function buildFormField(field: FormField, value: any): object {
    return {
        [field]: value,
        [field + VALUE_NORMALIZED_SUFFIX]: normalizeFieldValue(field, value)
    }
}

function buildFormFieldFromCookie(field: FormField, cookie: object): object {
    return buildFormField(field, cookie[field]);
}

export function buildFormFromCookie(configurationCookie: any, actualForm: object|undefined): any {
    return configurationCookie&&configurationCookie.authenticated? {
            ...buildFormFieldFromCookie(FormField.GIVENNAME, configurationCookie),
            ...buildFormFieldFromCookie(FormField.FAMILYNAME, configurationCookie),
            ...buildFormFieldFromCookie(FormField.EMAIL, configurationCookie),
            ...buildFormFieldFromCookie(FormField.PHONE, configurationCookie),
            ...buildFormFieldFromCookie(FormField.COMPANYNAME, configurationCookie),
            ...buildFormFieldFromCookie(FormField.TITLE, configurationCookie)
        }:
        (actualForm?{...actualForm}:undefined)
}

export function reducer(state: ContactData = initialState, action: FetchAction | FormAction | CountryCodeAction) {
    switch (action.type) {
        case ActionType.RETRIEVE_COUNTRY_CODE:
        case ActionType.LANGUAGE_SELECTION: {
            return {
                ...state,
                contact: {
                    ...state.contact,
                    ...buildFormField(FormField.COUNTRY, state.contact[FormField.COUNTRY] || (<unknown>action as CountryCodeAction).countrycode)
                }
            }
        }
        case ActionType.FETCH_CONFIGURATORPRODUCT: {
            switch (action.state) {
                case FetchState.SUCCESS: {
                    return {
                        ... initialState,
                        contact: buildFormFromCookie(getConfigurationCookie(), state.contact)
                    }
                }
                default:
                    return state;
            }
        }
        case ActionType.CONTACT_FORMCHANGE: {
            return {
                ...state,
                contact : {
                    ...state.contact,
                    ...action.payload
                }
            }
        }
        case ActionType.CONTACT_FORMREADY: {
            return {
                ...state,
                formReady: action.payload.formReady
            }
        }
        case ActionType.CONTACT_FORMSUBMITTING: {
            return {
                ...state,
                formSubmitting: true
            }
        }
        case ActionType.CONTACT_FORMSUBMITTED: {
            return {
                ...state,
                formSubmitting: false,
                formSubmitted: true,
                subscribed: true
            }
        }
        case ActionType.CONTACT_FORMCANNOTSUBMIT: {
            return {
                ...state,
                formSubmitting: false,
                formSubmitted: false,
                submissionError: true
            }
        }
        case ActionType.CHECK_CONFIGURATION: {
            switch (action.state) {
                case FetchState.SUCCESS: {
                    return {
                        ...state,
                        contact: buildFormFromCookie(getConfigurationCookie(), state.contact),
                    };
                }
                default:
                    return state;
            }
        }
        case ActionType.FETCH_CONFIGURATION: {
            switch (action.state) {
                case FetchState.SUCCESS: {
                    return {
                        ...state,
                        contact: buildFormFromCookie(getConfigurationCookie(), state.contact)
                    };
                }
                default:
                    return state;
            }
        }
        case ActionType.CANCEL_SUBSCRIBE: {
            return {
                ...state,
                formReady: false,
                formSubmitting: false,
                formSubmitted: false
            }
        }
        default:
            return state
    }
}

function normalizedForm(form: Object, type: ContactType | undefined): CustomerContact {
    return Object.keys(form)
        .filter(key => key.endsWith(VALUE_NORMALIZED_SUFFIX))
        .reduce((obj, key) => {
            return {
                ...obj,
                [key.slice(0, -VALUE_NORMALIZED_SUFFIX.length)]: form[key]
            };
        }, type?{contacttype: type}:{});
}

export function buildCustomerContact(form: ContactData | undefined, type: ContactType | undefined): CustomerContact {
    return form&&form.formReady?normalizedForm(form.contact, type):{}
}

interface FormAction
{
    type: ActionType.CONTACT_FORMCHANGE | ActionType.CONTACT_FORMREADY | ActionType.CONTACT_FORMSUBMITTING | ActionType.CONTACT_FORMSUBMITTED | ActionType.CONTACT_FORMCANNOTSUBMIT
    payload: object
}

export function contactFormChange(data: Object): FormAction {
    return { type: ActionType.CONTACT_FORMCHANGE, payload: { ...data } }
}

export function contactFormReady(ready:boolean): FormAction {
    return { type: ActionType.CONTACT_FORMREADY, payload: { formReady: ready } }
}

export function contactFormSubmitting(): FormAction {
    return { type: ActionType.CONTACT_FORMSUBMITTING, payload: { } }
}

export function contactFormSubmitted(subscribed?: boolean): FormAction {
    return { type: ActionType.CONTACT_FORMSUBMITTED, payload: { subscribed } }
}

export function contactFormCannotSubmit(): FormAction {
    return { type: ActionType.CONTACT_FORMCANNOTSUBMIT, payload: { } }
}


// WEBPACK FOOTER //
// ./src/contactData.ts